/**
 * Sculpt kernels — what one dab of the brush does to the ground itself.
 *
 * The mirror of `TerrainPaint`, over the canonical {@link Heightfield} instead of the paint map, and
 * pure for the same reason. Heights and paint are independent grids, which is what lets you re-grade
 * a slope without smearing the mulch bed you already drew on it.
 *
 * ## The four modes, and why these four
 *
 * `raise` / `lower` are the same operation with a sign, so Alt inverting the brush is not a special
 * case in the tool — it flips one number. `smooth` relaxes toward the local average and is the
 * corrective every sculpt tool needs, because freehand raising always leaves ridges. `flatten` is the
 * one that earns its place for *landscape* work specifically: a patio, a shed pad, a level lawn, and
 * a driveway all need ground that is flat at a chosen height rather than merely smooth, and no
 * combination of raise and smooth converges on that. It takes its target height from where the
 * stroke began, so you pick the level by choosing where to press the button.
 *
 * ## Displacement per dab, not per second
 *
 * `strengthMeters` is metres moved by a dab at full falloff. Because dabs are emitted per metre
 * travelled and per unit of time held (see `TerrainBrush`), and never per frame or per pointer
 * event, the same drag moves the same amount of earth on any machine.
 */

import type { Heightfield } from './TerrainTypes';
import { brushFalloff, dabGridBounds, type BrushPoint, type BrushShape } from './TerrainBrush';
import {
  clipRect,
  EMPTY_RECT,
  isEmptyRect,
  type SurfaceRect,
} from './TerrainSurface';

export type SculptMode = 'raise' | 'lower' | 'smooth' | 'flatten';

export const SCULPT_MODES: readonly SculptMode[] = ['raise', 'lower', 'smooth', 'flatten'];

/**
 * The colour each mode wears.
 *
 * Both the ring the brush drops on the ground and the pointer glyph read from here, so the two can
 * never come to disagree about what orange means — which they would, kept in a table each.
 */
export const SCULPT_MODE_COLORS: Record<SculptMode, number> = {
  raise: 0x8bd450,
  lower: 0xe8913c,
  smooth: 0x63c8ff,
  flatten: 0xc08bff,
};

/** `SCULPT_MODE_COLORS` as CSS hex, for the parts of the UI that are drawn in the DOM. */
export function sculptModeCss(mode: SculptMode): string {
  return `#${SCULPT_MODE_COLORS[mode].toString(16).padStart(6, '0')}`;
}

export interface SculptDabOptions {
  mode: SculptMode;
  /** Metres moved by a dab at full falloff. Scaled by the brush's `flow`. */
  strengthMeters: number;
  /** Target height for `flatten`, in metres. Sampled once at the start of the stroke. */
  targetHeight?: number;
  /** Hard limits so a runaway stroke cannot launch the ground into orbit. */
  minHeight?: number;
  maxHeight?: number;
}

/** Default vertical range, in metres relative to the site datum. */
export const DEFAULT_SCULPT_LIMIT = 30;

/**
 * Bounds that leave headroom over the terrain a template already generates, so a rolling site can
 * still be cut and filled, while a stuck key cannot produce a spike a kilometre high.
 */
export function sculptLimits(elevationAmplitude: number): { minHeight: number; maxHeight: number } {
  const span = Math.min(DEFAULT_SCULPT_LIMIT, Math.max(5, elevationAmplitude * 6 + 5));
  return { minHeight: -span, maxHeight: span };
}

/**
 * Apply one dab to the heightfield. Returns the vertex rectangle actually modified (empty when the
 * dab missed the site or moved nothing).
 *
 * `center` is in **terrain-local** metres.
 */
export function applySculptDab(
  field: Heightfield,
  center: BrushPoint,
  brush: BrushShape,
  options: SculptDabOptions,
): SurfaceRect {
  const bounds = clipRect(
    dabGridBounds(
      center,
      brush.radiusMeters,
      field.cols,
      field.rows,
      field.widthMeters,
      field.lengthMeters,
    ),
    field.cols,
    field.rows,
  );
  if (isEmptyRect(bounds)) return { ...EMPTY_RECT };

  const minHeight = options.minHeight ?? -DEFAULT_SCULPT_LIMIT;
  const maxHeight = options.maxHeight ?? DEFAULT_SCULPT_LIMIT;
  const flow = Math.min(1, Math.max(0, brush.flow));
  const displacement = options.strengthMeters * flow;

  // Relaxing modes read from a snapshot so the result does not depend on iteration order.
  const source = options.mode === 'smooth' ? readHeightRect(field, bounds) : null;
  let touched = false;
  let minX = bounds.x + bounds.width;
  let minY = bounds.y + bounds.height;
  let maxX = bounds.x;
  let maxY = bounds.y;

  for (let row = bounds.y; row < bounds.y + bounds.height; row += 1) {
    for (let col = bounds.x; col < bounds.x + bounds.width; col += 1) {
      const world = vertexToWorld(field, col, row);
      const distance = Math.hypot(world.x - center.x, world.z - center.z);
      const falloff = brushFalloff(distance, brush.radiusMeters, brush.hardness);
      if (falloff <= 0) continue;

      const index = row * field.cols + col;
      const current = field.heights[index] ?? 0;
      let next = current;

      switch (options.mode) {
        case 'raise':
          next = current + displacement * falloff;
          break;
        case 'lower':
          next = current - displacement * falloff;
          break;
        case 'smooth': {
          const average = neighbourhoodAverage(source, bounds, col, row);
          // Relaxation rate, not a displacement — smoothing has no natural metre scale.
          next = current + (average - current) * Math.min(1, falloff * flow);
          break;
        }
        case 'flatten': {
          const target = options.targetHeight ?? current;
          next = current + (target - current) * Math.min(1, falloff * flow);
          break;
        }
      }

      next = Math.min(maxHeight, Math.max(minHeight, next));
      if (next === current) continue;
      field.heights[index] = next;
      touched = true;
      if (col < minX) minX = col;
      if (col > maxX) maxX = col;
      if (row < minY) minY = row;
      if (row > maxY) maxY = row;
    }
  }

  if (!touched) return { ...EMPTY_RECT };
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

/** Terrain-local metres at a vertex. Matches the layout `buildTerrainGeometry` writes. */
export function vertexToWorld(field: Heightfield, col: number, row: number): { x: number; z: number } {
  const u = field.cols > 1 ? col / (field.cols - 1) : 0;
  const v = field.rows > 1 ? row / (field.rows - 1) : 0;
  return { x: (u - 0.5) * field.widthMeters, z: (v - 0.5) * field.lengthMeters };
}

/** Copy heights out of a rectangle, row-major. Used by the undo record and by `smooth`. */
export function readHeightRect(field: Heightfield, rect: SurfaceRect): Float32Array {
  const out = new Float32Array(rect.width * rect.height);
  for (let row = 0; row < rect.height; row += 1) {
    const src = (rect.y + row) * field.cols + rect.x;
    out.set(field.heights.subarray(src, src + rect.width), row * rect.width);
  }
  return out;
}

/** Write heights back into a rectangle. The inverse of {@link readHeightRect}. */
export function writeHeightRect(field: Heightfield, rect: SurfaceRect, heights: Float32Array): void {
  for (let row = 0; row < rect.height; row += 1) {
    const dst = (rect.y + row) * field.cols + rect.x;
    field.heights.set(heights.subarray(row * rect.width, (row + 1) * rect.width), dst);
  }
}

/** 3×3 average from a snapshot, clamped at the snapshot's edges. */
function neighbourhoodAverage(
  source: Float32Array | null,
  rect: SurfaceRect,
  col: number,
  row: number,
): number {
  if (!source) return 0;
  let total = 0;
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      const c = Math.min(rect.width - 1, Math.max(0, col + dx - rect.x));
      const r = Math.min(rect.height - 1, Math.max(0, row + dy - rect.y));
      total += source[r * rect.width + c] ?? 0;
    }
  }
  return total / 9;
}

/**
 * Surface normals straight from the heightfield, by central differences.
 *
 * Cheaper and steadier than re-deriving them from triangles: a partial edit only needs the normals
 * inside its own rectangle (plus a one-vertex skirt), and because the formula is closed-form the
 * normals of an edited region are identical to the ones the whole field would have produced. Rebuild
 * the terrain from scratch and it matches what you were just looking at, to the bit.
 */
export function heightfieldNormal(
  field: Heightfield,
  col: number,
  row: number,
): { x: number; y: number; z: number } {
  const dx = field.cols > 1 ? field.widthMeters / (field.cols - 1) : 1;
  const dz = field.rows > 1 ? field.lengthMeters / (field.rows - 1) : 1;
  const at = (c: number, r: number): number => {
    const cc = Math.min(field.cols - 1, Math.max(0, c));
    const rr = Math.min(field.rows - 1, Math.max(0, r));
    return field.heights[rr * field.cols + cc] ?? 0;
  };
  const slopeX = (at(col + 1, row) - at(col - 1, row)) / (2 * dx);
  const slopeZ = (at(col, row + 1) - at(col, row - 1)) / (2 * dz);
  const length = Math.hypot(slopeX, 1, slopeZ) || 1;
  return { x: -slopeX / length, y: 1 / length, z: -slopeZ / length };
}
